import {
  Box,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tooltip,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';

import { PolicyTypeEnum } from '../utils/constants';
import { OverAllPolicyType } from '../utils/types';
import { isInUIC } from '@Utils/common';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrgStore } from '@Models/useOrgModel';
import { RiskAIApi } from '@Apis';
import { PolicySubTypeEnum } from '@Types/index';

const PopperProps = {
  modifiers: [
    {
      name: 'flip',
      enabled: true,
    },
  ],
  usePortal: true, // Render tooltip in portal to avoid clipping
};
const RulesItem = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: any;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const boxShadow = colorStyle.shadow.thin;
  return (
    <Box
      boxShadow={boxShadow}
      border={1}
      borderColor={colorStyle.background.tertiary}
      backgroundColor={colorStyle.background.tertiary}
      px="8px"
      py="3px"
      borderRadius="3px"
      opacity={enabled ? '1' : '0.28'}
    >
      {children}
    </Box>
  );
};

interface ATPPolicyRule {
  isAsEnabled: boolean;
  isVsEnabled: boolean;
  isFbEnabled: boolean;
  isWtpEnabled: boolean;
  isDdaEnabled: boolean;
  isRaiEnabled: boolean;
  policyType: PolicyTypeEnum;
  [key: string]: any;
}

interface DLPPolicyRule {
  isDpEnabled: boolean;
  isKfEnabled: boolean;
  isSlfEnabled: boolean;
  policyType: PolicyTypeEnum;
  policySubType: number;
  [key: string]: any;
}

type Props = (
  | {
      type: OverAllPolicyType.ATP;
      policy: ATPPolicyRule;
    }
  | {
      type: OverAllPolicyType.DLP;
      policy: DLPPolicyRule;
    }
) & { isFirstRow?: boolean };

export const PolicyRulesCell = ({
  type,
  policy,
  isFirstRow = false,
}: Props) => {
  if (type === OverAllPolicyType.ATP) {
    return <ATPRules {...policy} isFirstRow={isFirstRow} />;
  } else {
    return <DLPRules {...policy} />;
  }
};

const ATPRules = ({
  isAsEnabled,
  isVsEnabled,
  isFbEnabled,
  isWtpEnabled,
  isDdaEnabled,
  isRaiEnabled,
  policyType,
  policySubType,
  isFirstRow = false,
}: ATPPolicyRule & { isFirstRow?: boolean }) => {
  const { t } = useTranslation();
  const [isAutoPopoverTimeoutDone, setIsAutoPopoverTimeoutDone] =
    useState(false);
  const [ciPopoverIsOpen, setCIPopoverIsOpen] = useState(isFirstRow);
  const { orgId } = useOrgStore();

  const isMailPolicy = [
    PolicyTypeEnum.Exchange,
    PolicyTypeEnum.ExchangeInline,
    PolicyTypeEnum.Gmail,
    PolicyTypeEnum.GmailInline,
  ].includes(policyType);

  const isSalesforcePolicy = [
    PolicyTypeEnum.SalesforceSandbox,
    PolicyTypeEnum.SalesforceProduction,
  ].includes(policyType);

  const enabledString = t('common.label.enabled');
  const disabledString = t('common.label.disabled');

  const asTooltipString = `${t('policy.filterName.advancedSpamProtection')} (${
    isAsEnabled ? enabledString : disabledString
  })`;
  const vsTooltipString = `${t('policy.filterName.malwareScanning')} (${
    isVsEnabled ? enabledString : disabledString
  })`;
  const fbTooltipString = `${t('policy.filterName.fileBlocking')} (${
    isFbEnabled ? enabledString : disabledString
  })`;
  const wtpTooltipString = `${t('policy.filterName.webReputation')} (${
    isWtpEnabled ? enabledString : disabledString
  })`;
  const vaTooltipString = `${t('policy.filterName.virtualAnalyzer')} (${
    isDdaEnabled ? enabledString : disabledString
  })`;
  const raiTooltipString = `${t('policy.filterName.riskAi')} (${
    isRaiEnabled ? enabledString : disabledString
  })`;

  const { data: anomalyConfigured, isLoading: anomalyQueryLoading } = useQuery({
    queryKey: ['anomalyConfigured', orgId],
    queryFn: () => RiskAIApi.getAnomalyConfigured(orgId),
    select: ({ data }) => data,
    staleTime: Infinity, // Never refetch unless manually invalidated
  });

  const handlePopoverChange = useCallback(
    (isOpen: boolean) => {
      if (isAutoPopoverTimeoutDone) {
        setCIPopoverIsOpen(isOpen);
      }
    },
    [isAutoPopoverTimeoutDone]
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!anomalyQueryLoading && !anomalyConfigured && isFirstRow) {
        setCIPopoverIsOpen(false);
      }
      setIsAutoPopoverTimeoutDone(true);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [anomalyConfigured, anomalyQueryLoading, isFirstRow]);

  return (
    <Stack direction="row" flexWrap="nowrap" spacing="6px">
      {isMailPolicy && (
        <Tooltip
          label={asTooltipString}
          shouldWrapChildren
          PopperProps={PopperProps}
        >
          <RulesItem enabled={isAsEnabled}>AS</RulesItem>
        </Tooltip>
      )}
      <Tooltip
        label={vsTooltipString}
        shouldWrapChildren
        PopperProps={PopperProps}
      >
        <RulesItem enabled={isVsEnabled}>MS</RulesItem>
      </Tooltip>
      {!isSalesforcePolicy && (
        <Tooltip
          label={fbTooltipString}
          shouldWrapChildren
          PopperProps={PopperProps}
        >
          <RulesItem enabled={isFbEnabled}>FB</RulesItem>
        </Tooltip>
      )}
      <Tooltip
        label={wtpTooltipString}
        shouldWrapChildren
        PopperProps={PopperProps}
      >
        <RulesItem enabled={isWtpEnabled}>WR</RulesItem>
      </Tooltip>
      {!isSalesforcePolicy && (
        <Tooltip
          label={vaTooltipString}
          shouldWrapChildren
          PopperProps={PopperProps}
        >
          <RulesItem enabled={isDdaEnabled}>VA</RulesItem>
        </Tooltip>
      )}
      {([PolicyTypeEnum.Exchange, PolicyTypeEnum.Gmail].includes(policyType) ||
        ([PolicyTypeEnum.ExchangeInline, PolicyTypeEnum.GmailInline].includes(
          policyType
        ) &&
          policySubType === PolicySubTypeEnum.inbound)) &&
        (!anomalyQueryLoading && !anomalyConfigured ? (
          <Popover
            placement="top-start"
            trigger="hover"
            isOpen={ciPopoverIsOpen}
          >
            <PopoverTrigger
              onMouseEnter={() => handlePopoverChange(true)}
              onMouseLeave={() => handlePopoverChange(false)}
            >
              <Box>
                <RulesItem enabled={isRaiEnabled}>CI</RulesItem>
              </Box>
            </PopoverTrigger>
            <PopoverContent
              width="304px"
              PopperProps={{ usePortal: true }}
              onMouseEnter={() => handlePopoverChange(true)}
              onMouseLeave={() => handlePopoverChange(false)}
            >
              <Text>{t('policy.riskAi.tip')}</Text>
              <Link
                href={
                  isInUIC()
                    ? t(
                        'dashboard.chart.overallThreatDetection.anomalies.action.hrefV1'
                      )
                    : t(
                        'dashboard.chart.overallThreatDetection.anomalies.action.hrefCAS'
                      )
                }
                target="_blank"
              >
                {t('common.action.label.learnMore')}
              </Link>
            </PopoverContent>
          </Popover>
        ) : (
          <Tooltip
            label={raiTooltipString}
            shouldWrapChildren
            PopperProps={PopperProps}
          >
            <RulesItem enabled={isRaiEnabled}>CI</RulesItem>
          </Tooltip>
        ))}
    </Stack>
  );
};

const DLPRules = ({
  isDpEnabled,
  isKfEnabled,
  isSlfEnabled,
  policyType,
  policySubType,
}: DLPPolicyRule) => {
  const { t } = useTranslation();
  const enabledString = t('common.label.enabled');
  const disabledString = t('common.label.disabled');
  const dpTooltipString = `${t('policy.filterName.dataProtection')} (${
    isDpEnabled ? enabledString : disabledString
  })`;
  const kfTooltipString = `${t('policy.filterName.keywordExtraction')} (${
    isKfEnabled ? enabledString : disabledString
  })`;
  const slfTooltipString = `${t('policy.filterName.boxShareLink')} (${
    isSlfEnabled ? enabledString : disabledString
  })`;

  //special logic: policyType=PolicyTypeEnum.Box and policySubType==1, is box share link policy
  return (
    <Stack direction="row" flexWrap="nowrap" spacing="6px">
      {!(policyType === PolicyTypeEnum.Box && policySubType === 1) && (
        <Tooltip
          label={dpTooltipString}
          shouldWrapChildren
          PopperProps={PopperProps}
        >
          <RulesItem enabled={isDpEnabled}>DP</RulesItem>
        </Tooltip>
      )}
      {policyType === PolicyTypeEnum.Box && policySubType !== 1 && (
        <Tooltip
          label={kfTooltipString}
          shouldWrapChildren
          PopperProps={PopperProps}
        >
          <RulesItem enabled={isKfEnabled}>KW</RulesItem>
        </Tooltip>
      )}

      {/* just for box share link policy */}
      {policyType === PolicyTypeEnum.Box && policySubType === 1 && (
        <Tooltip
          label={slfTooltipString}
          shouldWrapChildren
          PopperProps={PopperProps}
        >
          <RulesItem enabled={isSlfEnabled}>SLC</RulesItem>
        </Tooltip>
      )}
    </Stack>
  );
};
